import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Loader2, ShieldAlert, ShieldCheck, Plus, Trash2, Search } from 'lucide-react';
import PageHeader from '../../components/PageHeader';
import Button from '../../components/ui/Button';
import { Card, CardContent } from '../../components/ui/Card';
import cultivarService from '../../services/cultivar';
import organizationService, { type OrgSearchResult } from '../../services/organization';
import {
  CULTIVAR_APPROVER_ROLES,
  CULTIVAR_APPROVER_ROLE_LABELS,
  type CultivarApproverRole,
  type CultivarProgram,
  type CultivarProgramApprover,
} from '../../types/cultivar';
import { useModuleLicense } from '../../hooks/useModuleLicense';

interface AddForm {
  approver_org_id: number | null;
  approver_org_name: string;
  approver_role: CultivarApproverRole;
  required: boolean;
  region: string;
  country: string;
  notes: string;
}

const emptyForm = (): AddForm => ({
  approver_org_id: null,
  approver_org_name: '',
  approver_role: 'marketing',
  required: true,
  region: '',
  country: '',
  notes: '',
});

function ProgramApprovers() {
  const { id } = useParams<{ id: string }>();
  const programId = id ? parseInt(id, 10) : NaN;
  const navigate = useNavigate();
  const { hasAccess: canAdmin, loading: licenseLoading } = useModuleLicense('cultivar', 'admin');

  const [program, setProgram] = useState<CultivarProgram | null>(null);
  const [approvers, setApprovers] = useState<CultivarProgramApprover[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);

  const [searchTerm, setSearchTerm] = useState('');
  const [searching, setSearching] = useState(false);
  const [results, setResults] = useState<OrgSearchResult[]>([]);
  const [form, setForm] = useState<AddForm>(emptyForm());
  const [submitting, setSubmitting] = useState(false);
  const [removing, setRemoving] = useState<number | null>(null);

  const load = useCallback(async () => {
    if (!Number.isFinite(programId)) return;
    setLoading(true);
    setError(null);
    try {
      const [progResp, apprResp] = await Promise.all([
        cultivarService.getProgram(programId),
        cultivarService.listProgramApprovers(programId),
      ]);
      if (progResp.success && progResp.data) setProgram(progResp.data as CultivarProgram);
      if (apprResp.success && apprResp.data) setApprovers(apprResp.data as CultivarProgramApprover[]);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load approvers');
    } finally {
      setLoading(false);
    }
  }, [programId]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    const term = searchTerm.trim();
    if (!term) { setResults([]); return; }
    const t = setTimeout(async () => {
      setSearching(true);
      try {
        const resp = await organizationService.searchOrganizations({ name: term });
        if (resp.success && resp.data) setResults(resp.data);
      } finally {
        setSearching(false);
      }
    }, 250);
    return () => clearTimeout(t);
  }, [searchTerm]);

  const add = async () => {
    if (!form.approver_org_id) {
      setError('Choose an approver organisation first.');
      return;
    }
    setSubmitting(true);
    setError(null);
    setInfo(null);
    try {
      const resp = await cultivarService.addProgramApprover(programId, {
        approver_org_id: form.approver_org_id,
        approver_role: form.approver_role,
        required: form.required,
        region: form.region || null,
        country: form.country || null,
        notes: form.notes || null,
      });
      if (!resp.success) throw new Error(resp.error?.message || 'Save failed');
      setInfo(`Added ${form.approver_org_name} as ${CULTIVAR_APPROVER_ROLE_LABELS[form.approver_role]} approver.`);
      setForm(emptyForm());
      setSearchTerm('');
      setResults([]);
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to add approver');
    } finally {
      setSubmitting(false);
    }
  };

  const remove = async (a: CultivarProgramApprover) => {
    setRemoving(a.id);
    setError(null);
    setInfo(null);
    try {
      const resp = await cultivarService.removeProgramApprover(programId, a.approver_org_id, a.approver_role);
      if (!resp.success) throw new Error(resp.error?.message || 'Remove failed');
      setInfo(`Removed ${a.approver_org_name || `org #${a.approver_org_id}`}.`);
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to remove approver');
    } finally {
      setRemoving(null);
    }
  };

  if (licenseLoading || loading) {
    return <div className="flex items-center justify-center min-h-[40vh]"><Loader2 className="w-6 h-6 animate-spin text-gray-400" /></div>;
  }
  if (!canAdmin) {
    return (
      <div className="max-w-2xl mx-auto p-6">
        <Card><CardContent className="p-6 flex items-start gap-3">
          <ShieldAlert className="w-5 h-5 text-amber-600 mt-0.5" />
          <div>
            <h3 className="font-semibold mb-1">Cultivar admin seat required</h3>
            <p className="text-sm text-gray-600">Managing program approvers requires a cultivar admin licence seat.</p>
          </div>
        </CardContent></Card>
      </div>
    );
  }
  if (!program) {
    return (
      <div className="max-w-2xl mx-auto p-6">
        <Card><CardContent className="p-6">
          <p className="text-gray-700">{error || 'Program not found.'}</p>
          <Button variant="secondary" onClick={() => navigate('/cultivar/admin/programs')} className="mt-3">
            <ArrowLeft className="w-4 h-4 mr-2" /> Back to programs
          </Button>
        </CardContent></Card>
      </div>
    );
  }

  return (
    <>
      <PageHeader
        title={`Approvers — ${program.name}`}
        subtitle="Orgs that must sign off on every new order under this program before contracts go to the customer."
        icon={<ShieldCheck className="h-6 w-6 text-white" />}
      />
      <div className="max-w-5xl mx-auto p-6 space-y-4">
        <div className="flex items-center gap-2">
          <Button variant="secondary" onClick={() => navigate('/cultivar/admin/programs')}>
            <ArrowLeft className="w-4 h-4 mr-2" /> Programs
          </Button>
        </div>

        {error && <div className="bg-red-50 border border-red-200 text-red-800 rounded px-4 py-2 text-sm">{error}</div>}
        {info && <div className="bg-emerald-50 border border-emerald-200 text-emerald-800 rounded px-4 py-2 text-sm">{info}</div>}

        <Card>
          <CardContent className="p-4 space-y-3">
            <h3 className="font-semibold">Add approver</h3>
            <div className="grid grid-cols-12 gap-3">
              <div className="col-span-12 md:col-span-6 relative">
                <label className="text-sm block">
                  <span className="text-gray-700">Approver organisation</span>
                  {form.approver_org_id ? (
                    <div className="mt-1 flex items-center gap-2 bg-emerald-50 border border-emerald-200 rounded px-3 py-1.5">
                      <span className="text-sm">{form.approver_org_name}</span>
                      <button type="button" className="ml-auto text-gray-500 hover:text-gray-800"
                        onClick={() => setForm({ ...form, approver_org_id: null, approver_org_name: '' })}>
                        Change
                      </button>
                    </div>
                  ) : (
                    <div className="mt-1 flex items-center border rounded px-2">
                      <Search className="w-4 h-4 text-gray-400" />
                      <input value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        placeholder="Search organisations by name…"
                        className="flex-1 px-2 py-1.5 text-sm focus:outline-none" />
                      {searching && <Loader2 className="w-4 h-4 animate-spin text-gray-400" />}
                    </div>
                  )}
                </label>
                {results.length > 0 && !form.approver_org_id && (
                  <div className="absolute z-10 mt-1 bg-white border rounded shadow w-full max-h-64 overflow-auto">
                    {results.map((o) => (
                      <button key={o.id} type="button"
                        onClick={() => { setForm({ ...form, approver_org_id: o.id, approver_org_name: o.name }); setResults([]); setSearchTerm(''); }}
                        className="block w-full text-left px-3 py-2 text-sm hover:bg-gray-50">
                        <span className="font-medium">{o.name}</span>
                        {o.stakeholder_types?.length > 0 && <span className="text-gray-500 text-xs"> · {o.stakeholder_types.join(', ')}</span>}
                      </button>
                    ))}
                  </div>
                )}
              </div>
              <label className="text-sm col-span-6 md:col-span-2">
                <span className="text-gray-700">Role</span>
                <select value={form.approver_role}
                  onChange={(e) => setForm({ ...form, approver_role: e.target.value as CultivarApproverRole })}
                  className="mt-1 w-full border rounded px-2 py-1.5">
                  {CULTIVAR_APPROVER_ROLES.map((r) => (
                    <option key={r} value={r}>{CULTIVAR_APPROVER_ROLE_LABELS[r]}</option>
                  ))}
                </select>
              </label>
              <label className="text-sm col-span-6 md:col-span-2 flex items-center gap-2 mt-5">
                <input type="checkbox" checked={form.required}
                  onChange={(e) => setForm({ ...form, required: e.target.checked })} />
                <span className="text-gray-700">Required</span>
              </label>
              <label className="text-sm col-span-6 md:col-span-1">
                <span className="text-gray-700">Region</span>
                <input value={form.region}
                  onChange={(e) => setForm({ ...form, region: e.target.value })}
                  className="mt-1 w-full border rounded px-2 py-1.5" />
              </label>
              <label className="text-sm col-span-6 md:col-span-1">
                <span className="text-gray-700">Country</span>
                <input value={form.country}
                  onChange={(e) => setForm({ ...form, country: e.target.value })}
                  className="mt-1 w-full border rounded px-2 py-1.5" />
              </label>
              <label className="text-sm col-span-12">
                <span className="text-gray-700">Notes</span>
                <input value={form.notes}
                  onChange={(e) => setForm({ ...form, notes: e.target.value })}
                  className="mt-1 w-full border rounded px-2 py-1.5" />
              </label>
            </div>
            <div className="flex justify-end">
              <Button onClick={add} disabled={submitting}>
                {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Plus className="w-4 h-4 mr-2" />}
                Add approver
              </Button>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-0">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-left text-xs uppercase tracking-wide text-gray-500">
                <tr>
                  <th className="px-4 py-2">Org</th>
                  <th className="px-4 py-2">Role</th>
                  <th className="px-4 py-2">Required</th>
                  <th className="px-4 py-2">Scope</th>
                  <th className="px-4 py-2">Notes</th>
                  <th className="px-4 py-2"></th>
                </tr>
              </thead>
              <tbody>
                {approvers.length === 0 && (
                  <tr><td colSpan={6} className="px-4 py-6 text-center text-gray-500">No approvers configured — new orders dispatch contracts immediately on nursery accept.</td></tr>
                )}
                {approvers.map((a) => (
                  <tr key={a.id} className="border-t">
                    <td className="px-4 py-3 font-medium">{a.approver_org_name || `Org #${a.approver_org_id}`}</td>
                    <td className="px-4 py-3">{CULTIVAR_APPROVER_ROLE_LABELS[a.approver_role]}</td>
                    <td className="px-4 py-3">{a.required ? 'Yes' : 'No'}</td>
                    <td className="px-4 py-3 text-gray-600">{[a.region, a.country].filter(Boolean).join(' / ') || '—'}</td>
                    <td className="px-4 py-3 text-gray-600 max-w-xs truncate" title={a.notes || ''}>{a.notes || ''}</td>
                    <td className="px-4 py-3 text-right">
                      <Button variant="ghost" size="sm" onClick={() => remove(a)} disabled={removing === a.id}
                        className="text-error-600 hover:bg-error-50">
                        {removing === a.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CardContent>
        </Card>
      </div>
    </>
  );
}

export default ProgramApprovers;
